import algoliasearch from "algoliasearch";
import invariant from "assert";
import { camelize } from "camelscore";
import { find } from "lodash";
import i18n from "i18next";
import { Id } from "../lib/Id";
import { StrataCountsType, allStratas, strataCounts } from "./Listings/stratas";
import type { Response as AlgoliaResponse } from "./Response";
import { yieldRejectionsUntilSuccess } from "../lib/async_helpers";
// NOTE: The higher you make this number, the worse the performance of the Show/Hide Filters
// animation will be. 60 is very smooth, 100 is passable, 120+ is noticeably janky. Ideally,
// we'd like to have this number be 20-40.
const DEFAULT_HITS_PER_PAGE = 80;
const PAGE_BETWEEN_SEARCH_AND_BROWSE = Math.ceil(1000 / DEFAULT_HITS_PER_PAGE);
const ATTRIBUTES_FOR_FACETING = [
  "strata",
  "size",
  "category",
  "category_size",
  "category_path",
  "category_path_size",
  "designers.id",
  "location",
  "marketplace",
  "badges",
  "price_i",
  "sold_price",
];
type Cursor = string;

export type PageOfResults = {
  hits: Array<Record<string, any>>;
  strataCounts?: StrataCountsType;
  position: number | Cursor;
  nbHits: number;
  nbPages: number;
};

export type Rejection = {
  rejection: any;
};

export class AlgoliaPager {
  query: string;

  filters: string;

  // an Algolia filter string, not one of our feed filter objects!
  hitsPerPage: number;

  searchIndex: Record<string, any>;

  browseIndex: Record<string, any>;

  constructor(
    indexName: string | null | undefined,
    query: string,
    filters: string,
    hitsPerPage: number = DEFAULT_HITS_PER_PAGE,
  ) {
    this.query = query;
    this.filters = filters;
    this.hitsPerPage = hitsPerPage;
    const algoliaAppId = window.PUBLIC_CONFIG.algolia.app_id;
    const searchClient = algoliasearch(
      algoliaAppId,
      window.PUBLIC_CONFIG.algolia.public_search_key,
    );
    const browseClient = algoliasearch(
      algoliaAppId,
      window.PUBLIC_CONFIG.algolia.public_browse_key,
    );
    this.searchIndex = searchClient.initIndex(indexName || "");
    this.browseIndex = browseClient.initIndex(indexName || "");
  }

  static async *listingsSoldBy(
    sellerIds: Array<Id>,
  ): AsyncIterator<PageOfResults | Rejection> {
    if (sellerIds.length === 0) {
      return;
    }

    const sellerFilter = sellerIds
      .map((sellerId) => `user.id:'${sellerId}'`)
      .join(" OR ")
      .concat(` AND marketplace:${i18n.t("MARKETPLACE_ID")}`);
    const index = find(window.PUBLIC_CONFIG.algolia.indexes.listings, {
      name: "created_at desc",
    }) || {
      value: "",
    };
    const indexName = index.value;
    const client = new this(indexName, "", sellerFilter);
    yield* client.pages(0);
  }

  async *pages(
    position: number | string,
  ): AsyncGenerator<PageOfResults | Rejection, void, void> {
    if (typeof position === "number") {
      if (position < PAGE_BETWEEN_SEARCH_AND_BROWSE) {
        const [{ hits, nbPages, nbHits }, strataCounts] =
          yield* this.searchForPagePlusFetchStrataCounts(position);
        yield {
          position,
          strataCounts,
          nbHits,
          nbPages,
          hits: camelize(hits),
        };

        for (position += 1; position < nbPages; position++) {
          const { hits } = yield* this.searchForPage(position);
          yield {
            position,
            nbHits,
            nbPages,
            hits: camelize(hits),
          };
        }

        if (nbPages < PAGE_BETWEEN_SEARCH_AND_BROWSE) {
          return; // no need to browse
        }
      }

      invariant(position === PAGE_BETWEEN_SEARCH_AND_BROWSE);
      // @ts-expect-error browseIndex.browse does not exist in algoliasearch v4, so this
      // response cannot be typed until the browse path is migrated to browseObjects.
      const { hits, cursor, nbHits, nbPages } = yield* this.browseForPage(
        position,
      );
      yield {
        position,
        nbHits,
        nbPages,
        hits: camelize(hits),
      };
      position = cursor;
    }

    while (position) {
      // @ts-expect-error browseIndex.browseFrom does not exist in algoliasearch v4, so this
      // response cannot be typed until the browse path is migrated to browseObjects.
      const { hits, cursor, nbHits, nbPages } = yield* this.browseForCursor(
        position,
      );
      position = cursor;
      yield {
        position,
        nbHits,
        nbPages,
        hits: camelize(hits),
      };
    }
  }

  searchForPagePlusFetchStrataCounts = yieldRejectionsUntilSuccess((page) =>
    Promise.all([
      this.searchIndex.search(this.query, { ...this.params(), page }),
      this.fetchStrataCounts(),
    ]),
  );

  searchForPage = yieldRejectionsUntilSuccess(
    (page: number): Promise<AlgoliaResponse<Record<string, any>>> =>
      this.searchIndex.search(this.query, { ...this.params(), page }),
  );

  browseForPage = yieldRejectionsUntilSuccess((page) =>
    this.browseIndex.browse(this.query, { ...this.params(), page }),
  );

  browseForCursor = yieldRejectionsUntilSuccess((cursor) =>
    this.browseIndex.browseFrom(cursor),
  );

  params = () => ({
    filters: this.filters,
    hitsPerPage: this.hitsPerPage,
    facets: ATTRIBUTES_FOR_FACETING,
  });

  async fetchStrataCounts(): Promise<StrataCountsType> {
    const sameFiltersButForAllStratas = this.filters.replace(
      /\(strata[^\)]*\)/,
      allStratas,
    );
    // lol
    const resp = await this.searchIndex.search(this.query, {
      filters: sameFiltersButForAllStratas,
      hitsPerPage: 0,
      facets: ATTRIBUTES_FOR_FACETING,
    });
    const { strata } = resp.facets;
    return strataCounts(strata);
  }
}
